/**
 * LOUIS BAYRAD PDP JavaScript
 * Handles: color/size variant selection, AJAX add-to-cart, buy now,
 *          wishlist (localStorage), gallery, delivery checker,
 *          sticky cart, modals (T&C + size chart), accordion.
 *
 * Drop into: assets/louis-pdp.js
 * Loaded by: sections/main-product.liquid
 */

(function () {
  'use strict';

  // ─────────────────────────────────────────────────
  // READ PRODUCT DATA embedded by Liquid
  // ─────────────────────────────────────────────────
  function getJSON(id) {
    const el = document.getElementById(id);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  const PRODUCT_VARIANTS  = getJSON('louis-variants-json') || [];
  const GALLERY_IMAGES    = getJSON('louis-gallery-json') || [];
  const PRODUCT_META      = getJSON('louis-product-meta-json') || {};

  // ─────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────
  let selectedOptions    = {};   // { option1: 'Burgundy', option2: 'M' }
  let currentVariant     = null;
  let currentGalleryIdx  = 0;
  let selectedSize       = null;

  // ─────────────────────────────────────────────────
  // GLOBAL CAPTURE-PHASE GUARD — registered immediately (script top
  // level, not waiting for DOMContentLoaded) so it is the very first
  // click listener attached anywhere on the page.
  //
  // Razorpay Magic Checkout's "Buy Now" hook was bypassing the invisible
  // overlay guard nested inside the button. The overlay correctly became
  // the click target (confirmed via elementFromPoint), which means
  // Razorpay isn't just listening on the button in the bubble phase —
  // it's almost certainly using a capture-phase listener (likely on
  // `document`) combined with `event.target.closest('#louis-buy-now')`,
  // which still matches because the guard is a DESCENDANT of the button.
  //
  // A capture-phase listener on `window` is guaranteed to run before a
  // capture-phase listener on `document` (or anywhere else in the tree)
  // regardless of script/registration order, because the capture phase
  // travels window → document → ... → target. So this is the one place
  // we can be certain of seeing the click first, and `stopImmediatePropagation()`
  // here stops it from ever reaching Razorpay's listener at all.
  // ─────────────────────────────────────────────────
  window.addEventListener('click', function (e) {
    var target = e.target && e.target.closest
      ? e.target.closest('#louis-buy-now, #louis-add-to-bag')
      : null;
    if (!target) return;

    var hasSizes = document.querySelector('.louis-size-btn') !== null;
    if (hasSizes && !selectedSize) {
      e.preventDefault();
      e.stopImmediatePropagation();
      promptSizeSelect();
    }
  }, true);

  // Wishlist — persisted in localStorage
  // UNIFIED FORMAT with louis-plp.js: array of product objects {id, handle, title, url, price, image}
  const WL_KEY = 'tenhem_wishlist';
  function pdpWlGet() {
    try {
      var raw = JSON.parse(localStorage.getItem(WL_KEY)) || [];
      var valid = raw.filter(function (p) {
        return p && typeof p === 'object' && p.id && p.title && p.title !== 'undefined';
      });
      if (valid.length !== raw.length) {
        try { localStorage.setItem(WL_KEY, JSON.stringify(valid)); } catch (e) {}
      }
      return valid;
    } catch (e) { return []; }
  }
  function pdpWlSave(l){ try { localStorage.setItem(WL_KEY, JSON.stringify(l)); } catch (e) {} }
  function pdpWlHas(id){ return pdpWlGet().some(function (p) { return String(p.id) === String(id); }); }
  function pdpWlAdd(p) { var l = pdpWlGet(); if (!pdpWlHas(p.id)) { p.savedAt = Date.now(); l.unshift(p); pdpWlSave(l); } }
  function pdpWlRem(id){ pdpWlSave(pdpWlGet().filter(function (p) { return String(p.id) !== String(id); })); }

  // ─────────────────────────────────────────────────
  // INIT
  // ─────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', function () {
    initVariantSelectors();
    initSizeGuard();
    initLinkedColorSwatches();
    initGallery();
    initCartButtons();
    initWishlist();
    initDelivery();
    initAccordion();
    initModals();
    initStickyCart();
    initSizeChartUnits();
    initSizeChartTypes();
  });

  // ═══════════════════════════════════════════════════
  // VARIANT SELECTORS
  // ═══════════════════════════════════════════════════
  function initVariantSelectors() {
    // Seed from pre-selected variant (set by Liquid)
    const seedEl = document.getElementById('louis-selected-variant');
    if (seedEl) {
      const seedId = parseInt(seedEl.value, 10);
      currentVariant = PRODUCT_VARIANTS.find(function (v) { return v.id === seedId; });
      if (currentVariant) {
        currentVariant.options.forEach(function (opt, i) {
          selectedOptions['option' + (i + 1)] = opt;
        });
        // Do NOT seed selectedSize here — size must be explicitly chosen by the user.
        // selectedOptions.option2 is used for variant matching, but selectedSize
        // stays null until the customer clicks a size button.
        selectedSize = null;
      }
    }

    // Mark active size to match seeded variant
    if (currentVariant) {
      document.querySelectorAll('.louis-size-btn').forEach(function (btn) {
        if (btn.dataset.value === selectedSize) {
          btn.classList.add('active');
        }
      });
    }

    // ── SIZE BUTTONS ──
    document.querySelectorAll('.louis-size-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (this.classList.contains('sold-out')) return;

        var optIdx = parseInt(this.dataset.optionIndex, 10);
        var val    = this.dataset.value;
        var key    = 'option' + (optIdx + 1);

        document.querySelectorAll('.louis-size-btn').forEach(function (b) {
          b.classList.remove('active');
        });
        this.classList.add('active');

        selectedOptions[key] = val;
        selectedSize = val;

        // Clear size validation message
        var msgEl = document.getElementById('louis-size-validation');
        if (msgEl) msgEl.textContent = '';

        updateVariant();
        updateWishlistState();
        refreshSizeGuard();
      });
    });
  }

  // ═══════════════════════════════════════════════════
  // SIZE GUARD
  // Buy Now / Add to Bag must stay visually untouched (same label,
  // same colour, same position) — but until a size is chosen, an
  // invisible overlay sits on top of each button and intercepts the
  // click before it ever reaches the button itself. This blocks not
  // just our own add-to-cart/checkout logic, but also any third-party
  // script (e.g. Razorpay Magic Checkout) that attaches its own click
  // listener directly to the button — that listener simply never
  // fires because the real button never receives the click.
  // ═══════════════════════════════════════════════════
  function initSizeGuard() {
    ['louis-buy-now', 'louis-add-to-bag'].forEach(function (id) {
      var btn = document.getElementById(id);
      if (!btn || btn._tenhemGuard) return;

      // No wrapper element — wrapping broke the CSS Grid layout of
      // .louis-cart-row (Add to Bag / Wishlist). Instead, make the button
      // itself the positioning context and nest the guard inside it.
      btn.classList.add('louis-cta-has-guard');

      var guard = document.createElement('div');
      guard.className = 'louis-cta-guard';
      guard.setAttribute('aria-hidden', 'true');
      btn.appendChild(guard);

      guard.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        promptSizeSelect();
      });

      btn._tenhemGuard = guard;
    });

    refreshSizeGuard();
  }

  function refreshSizeGuard() {
    var hasSizes  = document.querySelector('.louis-size-btn') !== null;
    var needsSize = hasSizes && !selectedSize;

    ['louis-buy-now', 'louis-add-to-bag'].forEach(function (id) {
      var btn = document.getElementById(id);
      if (!btn || !btn._tenhemGuard) return;
      btn._tenhemGuard.style.display = needsSize ? 'block' : 'none';
    });
  }

  function updateVariant() {
    var found = PRODUCT_VARIANTS.find(function (v) {
      return Object.keys(selectedOptions).every(function (key) {
        var idx = parseInt(key.replace('option', ''), 10) - 1;
        return v.options[idx] === selectedOptions[key];
      });
    });

    if (!found) return;
    currentVariant = found;

    // Update hidden form input
    var variantInput = document.getElementById('louis-selected-variant');
    if (variantInput) variantInput.value = found.id;

    // Update URL
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', found.id);
      history.replaceState({}, '', url.toString());
    } catch (e) {}

    updatePrice(found);
    updateCartButtonState(found);
    updateSizeAvailability();
    updateWishlistState();
  }

  function updatePrice(variant) {
    var priceEl = document.getElementById('louis-price');
    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price);
    }
    var compareEl = document.getElementById('louis-compare-price');
    var badgeEl   = document.getElementById('louis-savings-badge');
    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent = formatMoney(variant.compare_at_price);
        compareEl.style.display = '';
        if (badgeEl) {
          var pct = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
          badgeEl.textContent = pct + '% OFF';
          badgeEl.style.display = '';
        }
      } else {
        compareEl.style.display = 'none';
        if (badgeEl) badgeEl.style.display = 'none';
      }
    }
  }

  function formatMoney(cents) {
    var amount = cents / 100;
    return '₹' + amount.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  function updateCartButtonState(variant) {
    var addBtn = document.getElementById('louis-add-to-bag');
    var buyBtn = document.getElementById('louis-buy-now');
    if (!variant.available) {
      if (addBtn) { addBtn.disabled = true; addBtn.textContent = 'Sold Out'; }
      if (buyBtn) { buyBtn.disabled = true; buyBtn.textContent = 'Sold Out'; }
    } else {
      if (addBtn) { addBtn.disabled = false; addBtn.textContent = 'Add to Bag'; }
      if (buyBtn) { buyBtn.disabled = false; buyBtn.textContent = 'Buy Now'; }
    }
  }

  // Show sold-out sizes — handles both single-option (size only) and
  // legacy two-option (color + size) products gracefully.
  function updateSizeAvailability() {
    var hasTwoOptions = PRODUCT_VARIANTS.length > 0 && PRODUCT_VARIANTS[0].options.length >= 2;
    var color = selectedOptions.option1;

    document.querySelectorAll('.louis-size-btn').forEach(function (btn) {
      var size = btn.dataset.value;
      var variantForSize;
      if (hasTwoOptions) {
        variantForSize = PRODUCT_VARIANTS.find(function (v) {
          return v.option1 === color && v.option2 === size;
        });
      } else {
        variantForSize = PRODUCT_VARIANTS.find(function (v) {
          return v.option1 === size;
        });
      }
      if (variantForSize && !variantForSize.available) {
        btn.classList.add('sold-out');
      } else {
        btn.classList.remove('sold-out');
      }
    });
  }

  // ═══════════════════════════════════════════════════
  // GALLERY
  // ═══════════════════════════════════════════════════
  function initGallery() {
    buildDots();
    updateGalleryDisplay(0);

    var gallery = document.getElementById('louis-gallery');
    if (!gallery) return;

    // Mobile swiping is handled entirely by native CSS scroll-snap
    // (overflow-x: auto + scroll-snap-type: x mandatory — see
    // louis-pdp.css). We used to ALSO measure touchstart/touchend and
    // fire our own galleryNav()->scrollTo() on top of that, which meant
    // a single swipe could trigger two competing scroll animations (the
    // native snap plus our manual scrollTo) racing each other — that's
    // what caused the image jumping/jitter on mobile. Native scroll-snap
    // alone handles the swipe correctly, so the manual touch handlers
    // have been removed; we only need to listen for scroll to keep the
    // dots/index in sync, whichever way the gallery was scrolled.
    var scrollSyncTimer = null;
    gallery.addEventListener('scroll', function () {
      // Debounce: read the settled position after scrolling/snapping
      // stops, rather than reacting to every intermediate frame — reading
      // mid-scroll was also contributing to the index jumping around.
      if (scrollSyncTimer) clearTimeout(scrollSyncTimer);
      scrollSyncTimer = setTimeout(function () {
        var idx = Math.round(gallery.scrollLeft / gallery.offsetWidth);
        if (idx !== currentGalleryIdx) {
          currentGalleryIdx = idx;
          updateDots(idx);
        }
      }, 100);
    }, { passive: true });
  }

  // Public — called by arrow buttons (onclick="galleryNav(1)")
  window.galleryNav = function (dir) {
    currentGalleryIdx = Math.max(0, Math.min(GALLERY_IMAGES.length - 1, currentGalleryIdx + dir));
    updateGalleryDisplay(currentGalleryIdx);
  };

  // ═══════════════════════════════════════════════════
  // LINKED COLOUR SWATCHES (PDP)
  // Each colour is a separate product; swatches navigate to sibling URLs.
  // Groups products by the 4-digit style number prefix in their title.
  // ═══════════════════════════════════════════════════

  function resizeCdnImage(src, px) {
    if (!src) return src;
    return src.replace(/(\.\w+)(\?|$)/, '_' + px + 'x' + px + '_crop_center$1$2');
  }

  function fetchSiblingProducts() {
    if (window._tenhemSiblingsCache) {
      return Promise.resolve(window._tenhemSiblingsCache);
    }
    if (window._tenhemSiblingsPending) {
      return window._tenhemSiblingsPending;
    }
    window._tenhemSiblingsPending = fetch(
      '/collections/all/products.json?limit=250&fields=handle,title,featured_image,product_type'
    )
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var map = {};
        (data.products || []).forEach(function (p) {
          var pt = p.product_type || '';
          if (!pt) return;
          if (!map[pt]) map[pt] = [];
          map[pt].push({
            handle:    p.handle,
            title:     p.title,
                        imgUrl:    (p.images && p.images[0]) ? resizeCdnImage(p.images[0].src, 60) : null,
            colorName: '' // products.json has no metafield data; label falls back to title
          });
        });
        window._tenhemSiblingsCache   = map;
        window._tenhemSiblingsPending = null;
        return map;
      })
      .catch(function () {
        window._tenhemSiblingsPending = null;
        return {};
      });
    return window._tenhemSiblingsPending;
  }

  // ── Derive short colour names from sibling titles ──────────────
  // Used as fallback when metafield colorName is not available.
  // Finds the longest common word-boundary prefix, then returns
  // what remains (e.g. "Midnight Navy", "Reserve Burgundy").
  function extractColorNames(siblings) {
    var titles = siblings.map(function (s) { return s.title || ''; });
    var prefix = titles[0] || '';
    for (var i = 1; i < titles.length; i++) {
      while (titles[i].indexOf(prefix) !== 0) {
        var cut = prefix.lastIndexOf(' ');
        prefix  = cut > 0 ? prefix.slice(0, cut) : '';
        if (!prefix) break;
      }
    }
    prefix = prefix.trim();
    return titles.map(function (t) {
      var name = prefix.length ? t.slice(prefix.length).trim() : t.trim();
      return name || t.trim();
    });
  }

  function initLinkedColorSwatches() {
    var currentHandle = PRODUCT_META.handle || '';
    var container     = document.getElementById('louis-linked-swatches');
    var block         = document.getElementById('louis-linked-color-block');
    if (!container) return;

    // Primary: server-rendered siblings JSON (Liquid, grouped by product.type,
    // colorName from color-pattern category metafield).
    var serverSiblings = getJSON('louis-siblings-json');
        if (serverSiblings && serverSiblings.length > 1) {
      renderPdpSwatches(serverSiblings, currentHandle, container, block);
      return;
    }

    // Fallback: JS fetch grouped by product_type (metafield color names not available here)
    var productType = PRODUCT_META.type || '';
    if (!productType) { if (block) block.style.display = 'none'; return; }
        fetchSiblingProducts().then(function (map) {
      var siblings = (map[productType] || []).map(function (s) {
        return { handle: s.handle, title: s.title, img: s.imgUrl, colorName: s.colorName || '' };
      });

      // products.json has no metafields, so read each sibling's colour name
      // from the louis-product-meta-json block on its own product page.
      return Promise.all(siblings.map(function (s) {
        if (s.handle === currentHandle) {
          s.colorName = PRODUCT_META.colorName || '';
          return s;
        }
        return fetch('/products/' + s.handle)
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var m = html.match(/louis-product-meta-json[^>]*>([\s\S]*?)<\/script>/);
            if (m) {
              try { s.colorName = JSON.parse(m[1]).colorName || ''; } catch (e) {}
            }
            return s;
          })
          .catch(function () { return s; });
      }));
    }).then(function (siblings) {
      renderPdpSwatches(siblings, currentHandle, container, block);
    });
  }

  function renderPdpSwatches(siblings, currentHandle, container, block) {
    if (!siblings || siblings.length <= 1) {
      if (block) block.style.display = 'none';
      return;
    }

    // Use metafield colorName when available; fall back to title-prefix extraction.
    // Shopify only resolves metaobject references on the main product context —
    // siblings fetched via collection loop may have empty colorName.
    var hasMetafieldNames = siblings.some(function (s) { return s.colorName && s.colorName.trim(); });
    var displayNames = hasMetafieldNames
      ? siblings.map(function (s) { return s.colorName || s.title || ''; })
      : extractColorNames(siblings);

    // Update the "Colour: X" header label to the current product's colour name
    var labelEl   = document.getElementById('louis-color-label');
    var activeIdx = siblings.findIndex(function (s) { return s.handle === currentHandle; });
    if (labelEl && activeIdx >= 0) {
      labelEl.textContent = displayNames[activeIdx];
    }

    container.innerHTML = '';
    siblings.forEach(function (sib, i) {
      var isActive  = sib.handle === currentHandle;
      var colorName = displayNames[i];
      var a         = document.createElement('a');
      a.href        = '/products/' + sib.handle;
      a.className   = 'louis-linked-swatch' + (isActive ? ' active' : '');
      a.setAttribute('aria-label', colorName);

      // Image circle
      var imgSrc = sib.img || sib.imgUrl || null;
      if (imgSrc) {
        var img    = document.createElement('img');
        img.src    = imgSrc;
        img.alt    = colorName;
        img.width  = 28;
        img.height = 28;
        a.appendChild(img);
      }

      // Colour name label beneath the circle
      var nameSpan         = document.createElement('span');
      nameSpan.className   = 'louis-swatch-name';
      nameSpan.textContent = colorName;
      a.appendChild(nameSpan);

      // Hovering updates the header label; mouseleave restores the active one
      a.addEventListener('mouseenter', function () {
        if (labelEl) labelEl.textContent = colorName;
      });
      a.addEventListener('mouseleave', function () {
        if (labelEl) labelEl.textContent = displayNames[activeIdx >= 0 ? activeIdx : i];
      });

      container.appendChild(a);
    });
  }

  function updateGalleryDisplay(index) {
    var isDesktop = window.innerWidth > 900;

    if (isDesktop) {
      // Desktop: all tiles visible — scroll gallery column to the target tile
      var tiles = document.querySelectorAll('.louis-gallery-tile');
      // snap to the even tile of the pair (left tile of each row)
      var pairStart = index % 2 === 0 ? index : index - 1;
      var targetTile = tiles[pairStart];
      if (targetTile) {
        targetTile.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // Mobile: horizontal snap scroll
      var gallery = document.getElementById('louis-gallery');
      if (gallery) {
        gallery.scrollTo({ left: index * gallery.offsetWidth, behavior: 'smooth' });
      }
      updateDots(index);
    }
  }

  function buildDots() {
    var dotsContainer = document.getElementById('louis-gallery-dots');
    if (!dotsContainer || GALLERY_IMAGES.length <= 1) return;
    dotsContainer.innerHTML = '';
    GALLERY_IMAGES.forEach(function (_, i) {
      var dot = document.createElement('div');
      dot.className = 'louis-gallery-dot' + (i === 0 ? ' active' : '');
      dot.addEventListener('click', function () { window.galleryNav(i - currentGalleryIdx); });
      dotsContainer.appendChild(dot);
    });
  }

  function updateDots(activeIndex) {
    var dots = document.querySelectorAll('.louis-gallery-dot');
    dots.forEach(function (dot, i) {
      dot.classList.toggle('active', i === activeIndex);
    });
  }

  // ═══════════════════════════════════════════════════
  // ADD TO CART + BUY NOW
  // ═══════════════════════════════════════════════════
  function initCartButtons() {
    var addBtn = document.getElementById('louis-add-to-bag');
    var buyBtn = document.getElementById('louis-buy-now');

    if (addBtn) {
      addBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var hasSizes = document.querySelector('.louis-size-btn') !== null;
        if (!currentVariant || (hasSizes && !selectedSize)) {
          promptSizeSelect();
          return;
        }
        var btn = this;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        addToCart(currentVariant.id, 1)
          .then(function () {
            btn.textContent = 'Added!';
            showStickyCart();
            updateHeaderCartCount();
            // Open the cart drawer
            document.dispatchEvent(new CustomEvent('tenhem:cart:open'));
            setTimeout(function () {
              btn.disabled = false;
              btn.textContent = 'Add to Bag';
            }, 1800);
          })
          .catch(function () {
            btn.disabled = false;
            btn.textContent = 'Add to Bag';
          });
      });
    }

    if (buyBtn) {
      buyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var hasSizes = document.querySelector('.louis-size-btn') !== null;
        if (!currentVariant || (hasSizes && !selectedSize)) {
          promptSizeSelect();
          return;
        }
        var btn = this;
        btn.disabled = true;
        btn.textContent = 'Processing…';
        addToCart(currentVariant.id, 1)
          .then(function () {
            window.location.href = '/checkout';
          })
          .catch(function () {
            btn.disabled = false;
            btn.textContent = 'Buy Now';
          });
      });
    }
  }

  function promptSizeSelect() {
    var msgEl = document.getElementById('louis-size-validation');
    if (msgEl) {
      msgEl.textContent = 'Please select a size';
      msgEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function addToCart(variantId, qty) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: variantId, quantity: qty })
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart error');
      return res.json();
    });
  }

  function updateHeaderCartCount() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = cart.item_count || 0;
        // Update LOUIS BAYRAD header bag badge + any other count elements
        document.querySelectorAll(
          '.louis-bag-count, [data-cart-count], .cart-count, .js-cart-count, [data-header-cart-quantity]'
        ).forEach(function (el) {
          el.textContent = count;
          el.style.display = count > 0 ? 'flex' : 'none';
        });
        // Fire event so louis-header.js updateCartCount listener also runs
        document.dispatchEvent(new CustomEvent('tenhem:cart:updated', { detail: cart }));
      })
      .catch(function () {});
  }

  // ═══════════════════════════════════════════════════
  // STICKY CART
  // ═══════════════════════════════════════════════════
  function initStickyCart() {
    // On page load, show sticky cart only if cart already has items
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart.item_count > 0) showStickyCart(false);
      })
      .catch(function () {});
  }

  function showStickyCart(updateMeta) {
    var sticky = document.getElementById('louis-sticky-cart');
    if (!sticky) return;

    sticky.classList.add('visible');
    document.body.classList.add('louis-has-sticky-cart');

    if (updateMeta === false) return;

    // ── Update sticky bar content from current variant ──
    var titleEl = document.querySelector('.louis-product-title');
    var nameEl  = sticky.querySelector('.louis-sticky-product-name');
    var metaEl  = sticky.querySelector('.louis-sticky-product-meta');
    var thumbEl = sticky.querySelector('.louis-sticky-thumb img');

    if (nameEl && titleEl) {
      nameEl.textContent = titleEl.textContent.trim();
    }

    if (metaEl && currentVariant) {
      var color    = selectedOptions.option1 ? selectedOptions.option1 + ' · ' : '';
      var size     = selectedSize ? 'Size ' + selectedSize + ' · ' : '';
      metaEl.textContent = color + size + formatMoney(currentVariant.price);
    }

    // Swap thumbnail to show current colour image
    if (thumbEl) {
      var activeTile = document.querySelector('.louis-gallery-tile img');
      if (activeTile) thumbEl.src = activeTile.src;
    }
  }

  // Expose so Shopify theme editor re-init can call it
  window.tenhemShowStickyCart = showStickyCart;

  // ═══════════════════════════════════════════════════
  // WISHLIST  (unified format — same as louis-plp.js)
  // Stores: [{ id, handle, title, url, price, image, savedAt }]
  // ═══════════════════════════════════════════════════
  function pdpGetProductData() {
    return {
      id:     (document.getElementById('louis-product-id')    || {}).value || '',
      handle: (document.getElementById('louis-product-handle')|| {}).value || '',
      title:  (document.getElementById('louis-product-title') || {}).value || '',
      url:    (document.getElementById('louis-product-url')   || {}).value || window.location.pathname,
      price:  currentVariant
                ? String(currentVariant.price)
                : ((document.getElementById('louis-product-price') || {}).value || ''),
      image:  (document.getElementById('louis-product-image') || {}).value || ''
    };
  }

  function initWishlist() {
    var wishlistBtn = document.getElementById('louis-wishlist-btn');
    if (!wishlistBtn) return;

    /* Restore state on load */
    var productId = (document.getElementById('louis-product-id') || {}).value;
    if (productId && pdpWlHas(productId)) {
      wishlistBtn.textContent = '♥';
      wishlistBtn.classList.add('active');
    }

    wishlistBtn.addEventListener('click', function () {
      /* Size must be selected first */
      if (!selectedSize) {
        var msgEl = document.getElementById('louis-size-validation');
        if (msgEl) { msgEl.textContent = 'Please select your size first'; }
        return;
      }

      var product  = pdpGetProductData();
      var statusEl = document.getElementById('louis-wishlist-status');

      if (pdpWlHas(product.id)) {
        pdpWlRem(product.id);
        this.textContent = '♡';
        this.classList.remove('active');
        if (statusEl) { statusEl.textContent = 'Removed from wishlist'; statusEl.classList.remove('success'); }
      } else {
        pdpWlAdd(product);
        this.textContent = '♥';
        this.classList.add('active');
        if (statusEl) { statusEl.textContent = 'Saved to wishlist — Size ' + selectedSize; statusEl.classList.add('success'); }
      }

      /* Sync count badge in header */
      var count = pdpWlGet().length;
      document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
        el.textContent = count > 0 ? count : '';
        el.style.display = count > 0 ? '' : 'none';
      });
    });
  }

  function updateWishlistState() {
    var btn      = document.getElementById('louis-wishlist-btn');
    var statusEl = document.getElementById('louis-wishlist-status');
    if (!btn) return;

    var productId = (document.getElementById('louis-product-id') || {}).value;
    if (productId && pdpWlHas(productId)) {
      btn.textContent = '♥';
      btn.classList.add('active');
      if (statusEl && selectedSize) { statusEl.textContent = 'In your wishlist'; statusEl.classList.add('success'); }
    } else {
      btn.textContent = '♡';
      btn.classList.remove('active');
      if (statusEl) { statusEl.textContent = ''; statusEl.classList.remove('success'); }
    }
  }

  // ═══════════════════════════════════════════════════
  // DELIVERY CHECKER
  // ═══════════════════════════════════════════════════
  function initDelivery() {
    var configEl = document.getElementById('louis-delivery-config');
    var config   = configEl ? (getJSON('louis-delivery-config') || {}) : {};

    window._tenhemDeliveryConfig = config;

    var checkBtn  = document.getElementById('louis-check-btn');
    var pinInput  = document.getElementById('louis-pin-input');

    if (checkBtn) checkBtn.addEventListener('click', checkDelivery);
    if (pinInput) {
      pinInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') checkDelivery();
      });
      // Auto-check if pre-filled
      if (pinInput.value.length === 6) checkDelivery();
    }
  }

  window.checkDelivery = function () {
    var pin       = (document.getElementById('louis-pin-input') || {}).value || '';
    var resultEl  = document.getElementById('louis-delivery-result');
    if (!resultEl) return;

    pin = pin.trim();
    if (!/^\d{6}$/.test(pin)) {
      resultEl.innerHTML = '<span style="color:#c00;font-size:12px">Please enter a valid 6-digit PIN code</span>';
      return;
    }

    var cfg   = window._tenhemDeliveryConfig || {};
    var days  = cfg.default_label || '5–7 days';

    if (cfg.express_pincodes && cfg.express_pincodes.includes(pin)) {
      days = cfg.express_label || 'Next day';
    } else if (cfg.standard_pincodes && cfg.standard_pincodes.includes(pin)) {
      days = cfg.standard_label || '3rd day';
    }

    resultEl.innerHTML =
      '<span class="louis-dot">✓</span> Get it by <strong>' + days + '</strong>';
  };

  // ═══════════════════════════════════════════════════
  // ACCORDION
  // ═══════════════════════════════════════════════════
  function initAccordion() {
    document.querySelectorAll('.louis-acc-head').forEach(function (head) {
      head.addEventListener('click', function () {
        var item = this.parentElement;
        item.classList.toggle('open');
      });
    });
  }

  // ═══════════════════════════════════════════════════
  // MODALS — T&C  +  SIZE CHART
  // ═══════════════════════════════════════════════════
  function initModals() {
    // Close on backdrop click
    ['louis-terms-modal', 'louis-sizechart-modal'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) {
        el.addEventListener('click', function (e) {
          if (e.target === el) window.tenhemCloseModal(id);
        });
      }
    });

    // ESC key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        window.tenhemCloseModal('louis-terms-modal');
        window.tenhemCloseModal('louis-sizechart-modal');
      }
    });
  }

  window.tenhemOpenTerms = function (type) {
    // reads from louis-tnc-json embedded by main-product.liquid
    // edit the content in theme editor → section settings → "T&C Modal Content"
    var T = getJSON('louis-tnc-json') || {};
    var t = T[type] || T['welcome'] || { title: 'Terms & Conditions', content: '' };
    var modal = document.getElementById('louis-terms-modal');
    if (!modal) return;
    modal.querySelector('.louis-modal-title').textContent  = t.title;
    modal.querySelector('.louis-modal-body').innerHTML     = t.content;
    modal.classList.add('open');
  };

  window.tenhemOpenSizeChart = function () {
    var modal = document.getElementById('louis-sizechart-modal');
    if (modal) modal.classList.add('open');
  };

  window.tenhemCloseModal = function (id) {
    var modal = document.getElementById(id);
    if (modal) modal.classList.remove('open');
  };

  // ═══════════════════════════════════════════════════
  // SIZE CHART — Unit toggle (inches / cm)
  // ═══════════════════════════════════════════════════
  function initSizeChartUnits() {
    document.querySelectorAll('.louis-unit-btn[data-group="unit"]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var unit = this.dataset.unit;
        document.querySelectorAll('.louis-unit-btn[data-group="unit"]').forEach(function (b) { b.classList.remove('active'); });
        this.classList.add('active');
        document.querySelectorAll('.louis-measure-in').forEach(function (el) {
          el.style.display = unit === 'in' ? '' : 'none';
        });
        document.querySelectorAll('.louis-measure-cm').forEach(function (el) {
          el.style.display = unit === 'cm' ? '' : 'none';
        });
      });
    });
  }

  // ═══════════════════════════════════════════════════
  // SIZE CHART — Type toggle (polo / tshirt)
  // ═══════════════════════════════════════════════════
  function initSizeChartTypes() {
    document.querySelectorAll('.louis-unit-btn[data-group="type"]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var type = this.dataset.type;
        document.querySelectorAll('.louis-unit-btn[data-group="type"]').forEach(function (b) { b.classList.remove('active'); });
        this.classList.add('active');
        document.querySelectorAll('.louis-type-panel').forEach(function (panel) {
          panel.style.display = panel.dataset.panel === type ? '' : 'none';
        });
      });
    });
  }

})();
